import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEur, fmtPct, fmtDate, label } from "@/lib/format";
import { KpiCard, PageHeader, Loading, StatusBadge, Money, Pct } from "@/components/Kpi";
import { DataTable, LinkCell } from "@/components/DataTable";
import { useLookups } from "@/lib/hooks";
import { useState } from "react";

export default function Portfolio() {
  const [company, setCompany] = useState("");
  const { companies } = useLookups();
  const { data, isLoading } = useQuery({ queryKey: ["portfolio", company], queryFn: () => api.get("/portfolio", { params: company ? { company_id: company } : {} }).then((r) => r.data) });
  if (isLoading || !data) return <Loading />;
  const cols = [
    { key: "code", label: "Codice", render: (r) => <LinkCell to={`/operazioni/${r.id}`} testId={`portfolio-link-${r.code}`}>{r.code}</LinkCell> },
    { key: "name", label: "Operazione" }, { key: "city", label: "Città" },
    { key: "status", label: "Stato", render: (r) => <StatusBadge s={r.status} /> },
    { key: "investimento", label: "Investimento", num: true, render: (r) => <Money v={r.investimento} /> },
    { key: "ricavi", label: "Ricavi", num: true, render: (r) => <Money v={r.ricavi} /> },
    { key: "utile", label: "Utile", num: true, render: (r) => <Money v={r.utile} signed /> },
    { key: "roi", label: "ROI", num: true, render: (r) => <Pct v={r.roi} /> },
    { key: "avanzamento_temporale", label: "Avanz. tempo", num: true, render: (r) => <Pct v={r.avanzamento_temporale} signed={false} /> },
    { key: "controlli_aperti", label: "Controlli", num: true, render: (r) => <span className={r.controlli_aperti ? "neg font-medium" : "pos"}>{r.controlli_aperti}</span> },
  ];
  return (
    <div>
      <PageHeader title="Dashboard portafoglio" subtitle="Visione complessiva delle operazioni accessibili"
        actions={<select value={company} onChange={(e) => setCompany(e.target.value)} className="input-editable w-56" data-testid="portfolio-company-filter"><option value="">Tutte le società</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard title="Operazioni attive" value={data.operazioni_attive} sub={`${data.operazioni_totali} totali`} to="/operazioni" testId="kpi-attive" />
        <KpiCard title="Investimento complessivo" value={fmtEur(data.investimento_complessivo)} testId="kpi-investimento" />
        <KpiCard title="Ricavi attesi" value={fmtEur(data.ricavi_attesi)} testId="kpi-ricavi" />
        <KpiCard title="Margine previsto" value={fmtEur(data.margine_previsto)} tone={data.margine_previsto < 0 ? "neg" : "pos"} testId="kpi-margine" />
        <KpiCard title="ROI medio ponderato" value={fmtPct(data.roi_medio_ponderato)} testId="kpi-roi" />
        <KpiCard title="Capitale erogato" value={fmtEur(data.capitale_erogato)} testId="kpi-erogato" />
        <KpiCard title="Fabbisogno residuo" value={fmtEur(data.fabbisogno_residuo)} testId="kpi-fabbisogno" />
        <KpiCard title="In ritardo / oltre budget" value={`${data.operazioni_in_ritardo.length} / ${data.operazioni_oltre_budget.length}`} tone={data.operazioni_in_ritardo.length + data.operazioni_oltre_budget.length ? "neg" : "pos"} testId="kpi-alert" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold mb-3">Operazioni</h2>
          <DataTable columns={cols} rows={data.operazioni} testId="portfolio-table" />
        </div>
        <div className="space-y-6">
          <section className="bg-white border rounded-md p-4 shadow-sm" data-testid="portfolio-deadlines">
            <h3 className="font-heading font-semibold mb-3">Prossime scadenze</h3>
            {data.prossime_scadenze.length === 0 && <p className="text-sm text-slate-500">Nessuna scadenza imminente</p>}
            <ul className="space-y-2 text-sm">
              {data.prossime_scadenze.map((d) => (
                <li key={d.id} className="flex justify-between gap-2 border-b pb-2 last:border-0">
                  <Link to={`/operazioni/${d.project_id}/documenti`} className="hover:text-primary">{d.title}</Link>
                  <span className="text-slate-500 whitespace-nowrap">{fmtDate(d.due_date)}</span>
                </li>
              ))}
            </ul>
          </section>
          <section className="bg-white border rounded-md p-4 shadow-sm" data-testid="portfolio-alerts">
            <h3 className="font-heading font-semibold mb-3">Segnalazioni</h3>
            {data.operazioni_in_ritardo.length + data.operazioni_oltre_budget.length === 0 && <p className="text-sm pos">Nessuna operazione in ritardo o oltre budget</p>}
            {data.operazioni_in_ritardo.map((o) => <p key={o.id} className="text-sm neg">In ritardo: <Link className="underline" to={`/operazioni/${o.id}`}>{o.code}</Link> (fine prevista {fmtDate(o.date_end_plan)})</p>)}
            {data.operazioni_oltre_budget.map((o) => <p key={o.id} className="text-sm neg">Oltre budget: <Link className="underline" to={`/operazioni/${o.id}/conto-economico`}>{o.code}</Link></p>)}
          </section>
        </div>
      </div>
    </div>
  );
}
